import React from 'react';
import LCIcon from '@/components/common/LCIcon';

// ─── Formatters ──────────────────────────────────────────────────────────────

export const fmtCurrency = (v: number | null | undefined): string => {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
};

export const fmtPct = (v: number | null | undefined, digits = 1): string =>
  v == null || isNaN(v) ? '—' : `${(v * 100).toFixed(digits)}%`;

export const fmtPctRaw = (v: number | null | undefined, digits = 1): string =>
  v == null || isNaN(v) ? '—' : `${v.toFixed(digits)}%`;

export const fmtNumber = (v: number | null | undefined): string =>
  v == null ? '—' : Math.round(v).toLocaleString();

export const fmtPrice = (v: number | null | undefined): string =>
  v == null ? '—' : v.toFixed(2);

// ─── Status maps ─────────────────────────────────────────────────────────────

export const STATUS_LABEL: Record<string, string> = {
  GREEN: 'Green', AMBER_ALERT: 'Amber Alert', RED_ALERT: 'Red Alert',
  CRITICAL: 'Critical', HOUSE_CALL: 'House Call',
};

export const STATUS_FILL: Record<string, string> = {
  GREEN: '#1f8a4a', AMBER_ALERT: '#ffbf00', RED_ALERT: '#E50025',
  CRITICAL: '#B80020', HOUSE_CALL: '#000000',
};

const STATUS_CHIP_CLS: Record<string, string> = {
  GREEN:      'text-[#1f8a4a] bg-[rgba(31,138,74,0.10)]',
  AMBER_ALERT:'text-[#b6770b] bg-[rgba(255,191,0,0.16)]',
  RED_ALERT:  'text-[#E50025] bg-[rgba(229,0,37,0.10)]',
  CRITICAL:   'text-white bg-[#B80020]',
  HOUSE_CALL: 'text-white bg-black',
};

// Keep STATUS_CHIP for backwards compat (used in old code paths)
export const STATUS_CHIP: Record<string, string> = {
  GREEN: 'chip--green', AMBER_ALERT: 'chip--amber', RED_ALERT: 'chip--red',
  CRITICAL: 'chip--critical', HOUSE_CALL: 'chip--house',
};

function normalizeStatus(status: string | null | undefined): string {
  if (!status) return '';
  return status.toUpperCase().replace(/ /g, '_');
}

// ─── Shared class strings ─────────────────────────────────────────────────────

export const CLS = {
  // Chip base
  chip: 'inline-flex items-center gap-[5px] text-[9.5px] font-bold tracking-[0.08em] uppercase px-2 py-[3px] whitespace-nowrap',
  chipDot: 'w-1.5 h-1.5 rounded-full bg-current',
  chipOutline: 'bg-transparent border border-black/[0.18] text-[#333]',
  chipSuccess: 'text-[#1f8a4a] border-[rgba(31,138,74,0.32)]',
  chipSolidWarn: 'text-white bg-[#b6770b]',
  chipNeutral: 'text-[#333] bg-black/[0.06]',
  // Button base
  btn: 'font-sans font-bold tracking-[0.1em] uppercase border-0 cursor-pointer inline-flex items-center gap-2 whitespace-nowrap transition-colors disabled:opacity-45 disabled:cursor-not-allowed',
  btnSm: 'text-[10px] px-[11px] py-[7px] tracking-[0.09em]',
  btnMd: 'text-[11px] px-4 py-[10px]',
  btnPrimary: 'bg-[#E50025] text-white hover:bg-[#B80020]',
  btnOutline: 'bg-transparent text-black border border-black/[0.32] hover:bg-black hover:text-white hover:border-black',
  btnGhost: 'bg-transparent text-[#333] hover:text-[#E50025]',
  btnSecondary: 'bg-black text-white hover:bg-[#222]',
  // Table
  tableWrap: 'bg-white border border-black/[0.06] overflow-x-auto',
  table: 'w-full border-collapse text-[12.5px] [font-feature-settings:\'tnum\']',
  th: 'text-left text-[9.5px] font-bold tracking-[0.12em] uppercase text-[#7A7A76] px-3.5 py-3 border-b border-black/10 whitespace-nowrap select-none bg-[#FAF9F8]',
  thSortable: 'cursor-pointer hover:text-black',
  thSorted: 'text-black',
  td: 'px-3.5 py-3 border-b border-black/[0.05] align-middle',
  tdNum: 'text-right [font-feature-settings:\'tnum\']',
  tdMono: 'font-mono text-[11px] tracking-[0.02em] text-[#333]',
  tdClient: 'font-medium text-black',
  // Input
  field: 'flex flex-col gap-1.5',
  fieldLabel: 'text-[10px] tracking-[0.16em] uppercase font-bold text-[#333]',
  input: 'font-[inherit] text-[13.5px] font-light px-3 py-[10px] border border-black/[0.16] bg-white text-black transition-colors h-full w-full rounded-none focus:outline-none focus:border-[#E50025]',
  inputWrap: 'relative',
  inputIco: 'absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7A7A76] pointer-events-none',
  // Page
  page: 'px-9 pt-7 pb-[60px] w-full max-lg:px-5 max-lg:pt-4 max-lg:pb-12 max-[768px]:px-3.5 max-[768px]:pt-3 max-[768px]:pb-10',
  pageHead: 'flex justify-between items-end gap-6 mb-[22px] max-[768px]:flex-col max-[768px]:items-start max-[768px]:gap-2.5 max-[768px]:mb-3.5',
  pageH1: 'font-serif font-normal text-[32px] leading-tight tracking-[-0.015em] text-black m-0 max-lg:text-[26px] max-[480px]:text-[22px]',
  pageSub: 'text-[13px] text-[#7A7A76] mt-1.5 font-normal',
  metaRow: 'flex gap-[18px] flex-wrap justify-end',
  meta: 'inline-flex items-center gap-1.5 text-[11.5px] text-[#7A7A76]',
  // Eyebrow
  eyebrow: 'text-[10px] font-bold tracking-[0.16em] uppercase text-[#7A7A76] m-0 mb-1.5',
  // Surface
  surface: 'bg-white border border-black/[0.06]',
  surfacePad: 'px-6 py-[22px]',
  // Section title
  sectionTitle: 'flex items-center gap-3.5 my-[22px] mb-3',
  sectionH2: 'font-serif text-[22px] font-normal tracking-[-0.005em] m-0',
  sectionCount: 'text-[11px] text-[#7A7A76] tracking-[0.04em]',
  sectionRule: 'flex-1 border-t border-black/[0.08] ml-1',
  // Misc
  muted: 'text-[#7A7A76]',
  hr: 'border-0 border-t border-black/[0.08] my-[18px]',
  iconBtn: 'bg-transparent border-0 p-1.5 cursor-pointer text-[#333] inline-grid place-items-center hover:text-[#E50025] hover:bg-[#FAF9F8] transition-colors',
  badgeNum: 'inline-grid place-items-center min-w-[16px] h-4 px-1 text-[9.5px] font-bold bg-[#E50025] text-white rounded-full ml-1',
};

// ─── LCBtn ───────────────────────────────────────────────────────────────────

interface BtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'ghost' | 'secondary';
  size?: 'sm' | 'md';
  children: React.ReactNode;
}

export const LCBtn: React.FC<BtnProps> = ({ variant = 'primary', size = 'md', className = '', children, ...rest }) => {
  const variantCls = {
    primary: CLS.btnPrimary, outline: CLS.btnOutline,
    ghost: CLS.btnGhost, secondary: CLS.btnSecondary,
  }[variant];
  const sizeCls = size === 'sm' ? CLS.btnSm : CLS.btnMd;
  return (
    <button className={`${CLS.btn} ${variantCls} ${sizeCls} ${className}`} {...rest}>
      {children}
    </button>
  );
};

// ─── LCChip ──────────────────────────────────────────────────────────────────

interface ChipProps {
  status?: string | null;
  label?: string;
  className?: string;
  variant?: 'outline' | 'solid-warn' | 'success' | 'neutral';
}

export const LCChip: React.FC<ChipProps> = ({ status, label, className = '', variant }) => {
  const norm = normalizeStatus(status);
  let colorCls = CLS.chipNeutral;
  if (variant === 'outline') colorCls = CLS.chipOutline;
  else if (variant === 'solid-warn') colorCls = CLS.chipSolidWarn;
  else if (variant === 'success') colorCls = `${CLS.chipOutline} ${CLS.chipSuccess}`;
  else if (norm && STATUS_CHIP_CLS[norm]) colorCls = STATUS_CHIP_CLS[norm];
  const text = label ?? (norm ? (STATUS_LABEL[norm] || status) : '—');
  return (
    <span className={`${CLS.chip} ${colorCls} ${className}`}>
      <span className={CLS.chipDot} />{text}
    </span>
  );
};

// ─── LCCushionBar ─────────────────────────────────────────────────────────────

interface CushionBarProps {
  value: number | null | undefined;
  status?: string | null;
  big?: boolean;
}

export const LCCushionBar: React.FC<CushionBarProps> = ({ value, status, big }) => {
  if (value == null) return <span className={CLS.muted}>—</span>;
  const pct = Math.max(0, Math.min(100, value * 100));
  const norm = normalizeStatus(status);
  const fill = norm && STATUS_FILL[norm] ? STATUS_FILL[norm] : '#1f8a4a';
  return (
    <span className="flex items-center gap-2.5">
      <span className={`relative overflow-hidden bg-black/[0.07] ${big ? 'w-[220px] h-[10px]' : 'w-[100px] h-[6px]'}`}>
        <span className="absolute left-0 top-0 bottom-0 transition-[width] duration-[220ms]" style={{ width: `${pct}%`, background: fill }} />
      </span>
      <span className={`font-semibold ${big ? 'text-[18px] min-w-[56px]' : 'text-[11.5px] min-w-[38px]'}`}>
        {(value * 100).toFixed(1)}%
      </span>
    </span>
  );
};

// ─── LCSortHeader ─────────────────────────────────────────────────────────────

interface SortHeaderProps {
  label: string;
  field: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: (field: string) => void;
  className?: string;
}

export const LCSortHeader: React.FC<SortHeaderProps> = ({ label, field, active, dir, onClick, className = '' }) => (
  <th
    className={`${CLS.th} ${CLS.thSortable} ${active ? CLS.thSorted : ''} ${className}`}
    onClick={() => onClick(field)}
  >
    {label}
    <span className={`inline-block ml-[5px] text-[9px] ${active ? 'opacity-100 text-[#E50025]' : 'opacity-40'}`}>
      {active ? (dir === 'asc' ? '▲' : '▼') : '▲'}
    </span>
  </th>
);

export { LCIcon };
